import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Switch,
  Textarea,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Divider,
  IconButton,
  Tooltip,
  ButtonGroup,
  useToast,
} from "@chakra-ui/react";
import { ViewIcon, EditIcon, Code } from "@chakra-ui/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { EMAIL_TEMPLATE_STATUS } from "./constants/emailConstants";
import { validateEmailTemplate, getDefaultTemplateContent } from "./utils/EmailTemplateUtils";

const TemplateFormModal = ({
  isOpen,
  onClose,
  template = null,
  onSave,
  templateTypes = [],
  templateVariables,
  loading = false,
}) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    subject: "",
    htmlContent: "",
    textContent: "",
    status: EMAIL_TEMPLATE_STATUS.DRAFT,
    isDefault: false,
    description: "",
    fromEmail: "",
    fromName: "",
    replyTo: "",
    attachments: [],
  });
  const [variables, setVariables] = useState([]);
  const [loadingVariables, setLoadingVariables] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editorMode, setEditorMode] = useState("visual"); // 'visual', 'code', or 'html'
  const [originalHtmlContent, setOriginalHtmlContent] = useState(""); // Backup for complex HTML
  const quillRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);

  // Handle window resize for responsive editor
  useEffect(() => {
    const handleResize = () => {
      setIsResizing(true);
      setTimeout(() => setIsResizing(false), 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Enhanced Quill editor modules configuration for email UI
  const modules = {
    toolbar: [
      // Text formatting
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: ["small", false, "large", "huge"] }],

      // Text styling
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],

      // Text alignment
      [{ align: [] }],

      // Lists and indentation
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],

      // Advanced formatting
      ["blockquote", "code-block"],

      // Media and links
      ["link", "image"],

      // Script formatting
      [{ script: "sub" }, { script: "super" }],

      // Clean formatting
      ["clean"],
    ],
    clipboard: {
      // Clean up pasted content for email compatibility
      matchVisual: false,
    },
    history: {
      delay: 1000,
      maxStack: 50,
      userOnly: true,
    },
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "align",
    "list",
    "bullet",
    "indent",
    "blockquote",
    "code-block",
    "link",
    "image",
    "script",
  ];

  // Responsive modules for mobile devices
  const getResponsiveModules = () => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      return {
        toolbar: [
          // Simplified toolbar for mobile
          ["bold", "italic", "underline"],
          [{ color: [] }],
          [{ align: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "image"],
          ["clean"],
        ],
        clipboard: {
          matchVisual: false,
        },
        history: {
          delay: 1000,
          maxStack: 50,
          userOnly: true,
        },
      };
    }
    return modules;
  };

  // Custom email component insertion functions
  const insertEmailButton = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (range) {
      const buttonHtml = `
                <div style="text-align: center; margin: 20px 0;">
                    <a href="#" style="
                        display: inline-block;
                        padding: 12px 24px;
                        background-color: #007bff;
                        color: #ffffff;
                        text-decoration: none;
                        border-radius: 4px;
                        font-weight: bold;
                        font-size: 16px;
                        border: none;
                        cursor: pointer;
                    ">Click Here</a>
                </div>
            `;
      quill.clipboard.dangerouslyPasteHTML(range.index, buttonHtml);
    }
  };

  const insertEmailDivider = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (range) {
      const dividerHtml = `
                <div style="margin: 20px 0; text-align: center;">
                    <hr style="border: none; border-top: 2px solid #e0e0e0; width: 100%; margin: 0;">
                </div>
            `;
      quill.clipboard.dangerouslyPasteHTML(range.index, dividerHtml);
    }
  };

  const insertEmailSpacer = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (range) {
      const spacerHtml = `<div style="height: 30px; line-height: 30px;">&nbsp;</div>`;
      quill.clipboard.dangerouslyPasteHTML(range.index, spacerHtml);
    }
  };

  const insertSocialLinks = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (range) {
      const socialHtml = `
                <div style="text-align: center; margin: 20px 0;">
                    <a href="#" style="display: inline-block; margin: 0 10px; text-decoration: none;">
                        <img src="https://via.placeholder.com/32x32/3b5998/ffffff?text=f" alt="Facebook" style="width: 32px; height: 32px; border-radius: 4px;">
                    </a>
                    <a href="#" style="display: inline-block; margin: 0 10px; text-decoration: none;">
                        <img src="https://via.placeholder.com/32x32/1da1f2/ffffff?text=t" alt="Twitter" style="width: 32px; height: 32px; border-radius: 4px;">
                    </a>
                    <a href="#" style="display: inline-block; margin: 0 10px; text-decoration: none;">
                        <img src="https://via.placeholder.com/32x32/0077b5/ffffff?text=in" alt="LinkedIn" style="width: 32px; height: 32px; border-radius: 4px;">
                    </a>
                    <a href="#" style="display: inline-block; margin: 0 10px; text-decoration: none;">
                        <img src="https://via.placeholder.com/32x32/e4405f/ffffff?text=ig" alt="Instagram" style="width: 32px; height: 32px; border-radius: 4px;">
                    </a>
                </div>
            `;
      quill.clipboard.dangerouslyPasteHTML(range.index, socialHtml);
    }
  };

  // Quick insert buttons for common email elements
  const QuickInsertButtons = () => (
    <VStack spacing={3} align="stretch">
      <Text fontSize="sm" fontWeight="medium" color="gray.600">
        Quick Insert
      </Text>
      <VStack spacing={2} align="stretch">
        <Text
          fontSize="xs"
          color="gray.500"
          display={{ base: "block", lg: "none" }}
        >
          Quick Insert (Mobile)
        </Text>
        <HStack
          spacing={2}
          flexWrap="wrap"
          display={{ base: "flex", lg: "none" }}
        >
          <Button
            variant="outline"
            onClick={insertEmailButton}
            leftIcon={
              <Box as="span" fontSize="xs">
                🔘
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            CTA Button
          </Button>
          <Button
            variant="outline"
            onClick={insertEmailDivider}
            leftIcon={
              <Box as="span" fontSize="xs">
                ➖
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            Divider
          </Button>
          <Button
            variant="outline"
            onClick={insertEmailSpacer}
            leftIcon={
              <Box as="span" fontSize="xs">
                📏
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            Spacer
          </Button>
          <Button
            variant="outline"
            onClick={insertSocialLinks}
            leftIcon={
              <Box as="span" fontSize="xs">
                📱
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            Social Links
          </Button>
        </HStack>

        <Text
          fontSize="xs"
          color="gray.500"
          display={{ base: "none", lg: "block" }}
        >
          Quick Insert (Desktop)
        </Text>
        <ButtonGroup
          size="sm"
          spacing={2}
          orientation="vertical"
          display={{ base: "none", lg: "flex" }}
        >
          <Button
            variant="outline"
            onClick={insertEmailButton}
            leftIcon={
              <Box as="span" fontSize="xs">
                🔘
              </Box>
            }
          >
            CTA Button
          </Button>
          <Button
            variant="outline"
            onClick={insertEmailDivider}
            leftIcon={
              <Box as="span" fontSize="xs">
                ➖
              </Box>
            }
          >
            Divider
          </Button>
          <Button
            variant="outline"
            onClick={insertEmailSpacer}
            leftIcon={
              <Box as="span" fontSize="xs">
                📱
              </Box>
            }
          >
            Spacer
          </Button>
          <Button
            variant="outline"
            onClick={insertSocialLinks}
            leftIcon={
              <Box as="span" fontSize="xs">
                📱
              </Box>
            }
          >
            Social Links
          </Button>
        </ButtonGroup>
      </VStack>
      <VStack spacing={2} align="stretch">
        <Text
          fontSize="xs"
          color="gray.500"
          display={{ base: "block", lg: "none" }}
        >
          Email Templates (Mobile)
        </Text>
        <HStack
          spacing={2}
          flexWrap="wrap"
          display={{ base: "flex", lg: "none" }}
        >
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("header")}
            leftIcon={
              <Box as="span" fontSize="xs">
                📄
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            Header
          </Button>
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("footer")}
            leftIcon={
              <Box as="span" fontSize="xs">
                🦶
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            Footer
          </Button>
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("twoColumn")}
            leftIcon={
              <Box as="span" fontSize="xs">
                📰
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            2 Columns
          </Button>
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("imageText")}
            leftIcon={
              <Box as="span" fontSize="xs">
                🖼️
              </Box>
            }
            size="sm"
            flex="1 1 auto"
          >
            Image + Text
          </Button>
        </HStack>

        <Text
          fontSize="xs"
          color="gray.500"
          display={{ base: "none", lg: "block" }}
        >
          Email Templates (Desktop)
        </Text>
        <ButtonGroup
          size="sm"
          spacing={2}
          orientation="vertical"
          display={{ base: "none", lg: "flex" }}
        >
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("header")}
            leftIcon={
              <Box as="span" fontSize="xs">
                📄
              </Box>
            }
            textAlign="left"
            justifyContent="flex-start"
          >
            Header
          </Button>
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("footer")}
            leftIcon={
              <Box as="span" fontSize="xs">
                🦶
              </Box>
            }
            textAlign="left"
            justifyContent="flex-start"
          >
            Footer
          </Button>
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("twoColumn")}
            leftIcon={
              <Box as="span" fontSize="xs">
                📰
              </Box>
            }
            textAlign="left"
            justifyContent="flex-start"
          >
            2 Columns
          </Button>
          <Button
            variant="outline"
            onClick={() => insertPrebuiltTemplate("imageText")}
            leftIcon={
              <Box as="span" fontSize="xs">
                🖼️
              </Box>
            }
            textAlign="left"
            justifyContent="flex-start"
          >
            Image + Text
          </Button>
        </ButtonGroup>
      </VStack>
    </VStack>
  );

  const insertPrebuiltTemplate = (type) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (!range) return;

    let templateHtml = "";

    switch (type) {
      case "header":
        templateHtml = `
                    <div style="background-color: #f8f9fa; padding: 20px; text-align: center; border-bottom: 3px solid #007bff;">
                        <img src="https://via.placeholder.com/200x60/007bff/ffffff?text=Logo" alt="Company Logo" style="max-width: 200px; height: auto;">
                        <h1 style="margin: 10px 0 0 0; color: #333; font-size: 24px; font-weight: bold;">Company Name</h1>
                    </div>
                `;
        break;
      case "footer":
        templateHtml = `
                    <div style="background-color: #343a40; color: #ffffff; padding: 30px; text-align: center; margin-top: 40px;">
                        <p style="margin: 0 0 10px 0; font-size: 14px;">© 2025 Company Name. All rights reserved.</p>
                        <p style="margin: 0 0 15px 0; font-size: 12px; color: #adb5bd;">
                            123 Business St, Suite 100<br>
                            City, State 12345<br>
                            Phone: (555) 123-4567
                        </p>
                        <div style="margin: 15px 0;">
                            <a href="#" style="color: #17a2b8; text-decoration: none; margin: 0 10px; font-size: 12px;">Unsubscribe</a>
                            <a href="#" style="color: #17a2b8; text-decoration: none; margin: 0 10px; font-size: 12px;">Privacy Policy</a>
                            <a href="#" style="color: #17a2b8; text-decoration: none; margin: 0 10px; font-size: 12px;">Contact Us</a>
                        </div>
                    </div>
                `;
        break;
      case "twoColumn":
        templateHtml = `
                    <div style="display: table; width: 100%; margin: 20px 0;">
                        <div style="display: table-cell; width: 50%; padding: 0 10px; vertical-align: top;">
                            <h3 style="color: #333; margin: 0 0 15px 0;">Column 1 Title</h3>
                            <p style="margin: 0; line-height: 1.6; color: #666;">Add your content here. This is the left column of your two-column layout.</p>
                        </div>
                        <div style="display: table-cell; width: 50%; padding: 0 10px; vertical-align: top;">
                            <h3 style="color: #333; margin: 0 0 15px 0;">Column 2 Title</h3>
                            <p style="margin: 0; line-height: 1.6; color: #666;">Add your content here. This is the right column of your two-column layout.</p>
                        </div>
                    </div>
                `;
        break;
      case "imageText":
        templateHtml = `
                    <div style="display: table; width: 100%; margin: 20px 0;">
                        <div style="display: table-cell; width: 200px; padding: 0 20px 0 0; vertical-align: top;">
                            <img src="https://via.placeholder.com/180x120/007bff/ffffff?text=Image" alt="Featured Image" style="width: 100%; height: auto; border-radius: 8px;">
                        </div>
                        <div style="display: table-cell; vertical-align: top; padding: 0;">
                            <h3 style="color: #333; margin: 0 0 15px 0;">Featured Content</h3>
                            <p style="margin: 0 0 15px 0; line-height: 1.6; color: #666;">Add your description here. This layout combines an image with accompanying text content.</p>
                            <a href="#" style="display: inline-block; padding: 8px 16px; background-color: #007bff; color: #ffffff; text-decoration: none; border-radius: 4px; font-size: 14px;">Learn More</a>
                        </div>
                    </div>
                `;
        break;
    }

    if (templateHtml) {
      quill.clipboard.dangerouslyPasteHTML(range.index, templateHtml);
    }
  };

  // Load complete HTML templates
  const loadHtmlTemplate = (templateType) => {
    // Get the default template content for this specific type
    const defaultContent = getDefaultTemplateContent(templateType);
    
    if (defaultContent) {
      handleInputChange('htmlContent', defaultContent.htmlContent);
      if (defaultContent.subject) {
        handleInputChange('subject', defaultContent.subject);
      }
      if (defaultContent.textContent) {
        handleInputChange('textContent', defaultContent.textContent);
      }
      return;
    }

    // Fallback templates for types not in EmailTemplateUtils
    let template = "";

    switch (templateType) {
      case "welcome":
        template = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Welcome Email</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
    <div style="background-color: #2563eb; padding: 30px; text-align: center;">
        <h1 style="margin: 0; color: #ffffff; font-size: 28px;">Welcome {{first_name}}!</h1>
    </div>
    <div style="background-color: #ffffff; padding: 40px 30px;">
        <p style="color: #4b5563; line-height: 1.6; font-size: 16px;">
            Thank you for joining us! We're excited to have you on board.
        </p>
        <div style="text-align: center; margin: 30px 0;">
            <a href="{{login_url}}" style="display: inline-block; padding: 15px 30px; background-color: #2563eb; color: #ffffff; text-decoration: none; border-radius: 8px; font-weight: bold;">Get Started</a>
        </div>
    </div>
</body>
</html>`;
        break;
      case "newsletter":
        template = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Elio & Papas Newsletter</title>
    <style>
        @media only screen and (max-width: 600px) {
            .container { width: 100% !important; }
            .content { padding: 20px !important; }
        }
    </style>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f8fafc; line-height: 1.6;">
    <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);">
        
        <!-- Header -->
        <div style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); padding: 40px 30px; text-align: center;">
            <div style="font-size: 48px; margin-bottom: 10px;">📰</div>
            <h1 style="margin: 0; color: #ffffff; font-size: 32px; font-weight: 700; letter-spacing: -0.5px;">Elio & Papas</h1>
            <p style="margin: 8px 0 0 0; color: #e0e7ff; font-size: 18px; font-weight: 400;">Newsletter</p>
        </div>
        
        <!-- Main Content -->
        <div style="padding: 50px 40px; background-color: #ffffff;">
            <p style="color: #374151; font-size: 18px; margin: 0 0 30px 0; font-weight: 500;">Dear {{user_name}},</p>
            
            <h2 style="color: #1e40af; font-size: 24px; margin: 0 0 20px 0; font-weight: 600;">
                <span style="font-size: 28px; margin-right: 8px;">📢</span>Latest Updates
            </h2>
            
            <!-- Newsletter Content -->
            <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 12px; padding: 30px; margin: 30px 0; border-left: 4px solid #3b82f6;">
                <h3 style="color: #1e40af; font-size: 20px; margin: 0 0 15px 0; font-weight: 600;">
                    <span style="font-size: 24px; margin-right: 8px;">✨</span>What's New
                </h3>
                <p style="color: #4b5563; font-size: 15px; margin: 0; line-height: 1.6;">
                    Your newsletter content goes here. Share exciting updates, news, and announcements with your subscribers.
                </p>
            </div>
            
            <!-- Call to Action -->
            <div style="text-align: center; margin: 40px 0;">
                <a href="{{newsletter_url}}" style="display: inline-block; padding: 18px 36px; background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: #ffffff; text-decoration: none; border-radius: 12px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4);">
                    <span style="font-size: 18px; margin-right: 8px;">📖</span>Read More
                </a>
            </div>
        </div>
        
        <!-- Footer -->
        <div style="background-color: #f8fafc; padding: 30px 40px; border-top: 1px solid #e5e7eb;">
            <div style="text-align: center; margin-bottom: 20px;">
                <p style="color: #374151; font-size: 16px; margin: 0 0 8px 0; font-weight: 500;">
                    Best regards,<br>
                    <strong style="color: #1e40af;">Elio & Papas Team</strong>
                </p>
            </div>
            
            <div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin: 20px 0;">
                <div style="display: flex; justify-content: center; gap: 30px; flex-wrap: wrap;">
                    <div style="text-align: center;">
                        <p style="color: #6b7280; font-size: 12px; margin: 0 0 4px 0; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px;">Email</p>
                        <p style="color: #1e40af; font-size: 14px; margin: 0; font-weight: 500;">elio@starntop.com</p>
                    </div>
                    <div style="text-align: center;">
                        <p style="color: #6b7280; font-size: 12px; margin: 0 0 4px 0; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px;">Website</p>
                        <p style="color: #1e40af; font-size: 14px; margin: 0; font-weight: 500;">starntop.com</p>
                    </div>
                </div>
            </div>
            
            <div style="text-align: center; margin-top: 20px;">
                <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                    © 2025 Elio & Papas. All rights reserved.
                </p>
            </div>
        </div>
    </div>
</body>
</html>`;
        break;
      case "promotional":
        template = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Special Offer - Elio & Papas</title>
</head>
<body style="font-family: Arial, sans-serif; margin: 0; padding: 0; background-color: #fff5f5;">
    <div style="max-width: 600px; margin: 0 auto; background-color: white; border-radius: 8px; overflow: hidden;">
        <div style="background: linear-gradient(135deg, #2563eb, #3b82f6); color: white; padding: 40px; text-align: center;">
            <h1 style="margin: 0; font-size: 32px;">Elio & Papas</h1>
            <p style="margin: 10px 0 0 0; font-size: 18px;">Special Offer - Limited Time Only!</p>
        </div>
        <div style="padding: 40px;">
            <p>Dear {{user_name}},</p>
            <p>Don't miss out on this amazing deal...</p>
            <div style="text-align: center; margin: 30px 0;">
                <a href="{{offer_url}}" style="display: inline-block; padding: 15px 30px; background-color: #2563eb; color: white; text-decoration: none; border-radius: 25px; font-weight: bold;">Claim Offer</a>
            </div>
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
                <p style="color: #6b7280; font-size: 12px; margin: 0;">
                    Email: elio@starntop.com | Website: starntop.com
                </p>
            </div>
        </div>
    </div>
</body>
</html>`;
        break;
      case "transactional":
        template = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Transaction Confirmation - Elio & Papas</title>
</head>
<body style="font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f8f9fa;">
    <div style="max-width: 600px; margin: 0 auto; background-color: white; border: 1px solid #dee2e6; border-radius: 8px;">
        <div style="background-color: #28a745; color: white; padding: 20px; text-align: center;">
            <h1 style="margin: 0;">Elio & Papas</h1>
            <p style="margin: 5px 0 0 0; opacity: 0.9;">Transaction Confirmed</p>
        </div>
        <div style="padding: 30px;">
            <p>Dear {{user_name}},</p>
            <p>Your transaction has been successfully processed.</p>
            <div style="background-color: #f8f9fa; padding: 20px; border-radius: 4px; margin: 20px 0;">
                <p><strong>Transaction ID:</strong> {{transaction_id}}</p>
                <p><strong>Amount:</strong> {{amount}}</p>
                <p><strong>Date:</strong> {{date}}</p>
            </div>
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
                <p style="color: #6b7280; font-size: 12px; margin: 0;">
                    Email: elio@starntop.com | Website: starntop.com
                </p>
            </div>
        </div>
    </div>
</body>
</html>`;
        break;
    }

    handleInputChange("htmlContent", template);
  };

  // Basic HTML formatting function
  const formatHtmlCode = () => {
    try {
      // Simple formatting - add line breaks and indentation
      let formatted = formData.htmlContent
        .replace(/></g, ">\n<")
        .replace(/^\s+|\s+$/g, "");

      // Basic indentation
      const lines = formatted.split("\n");
      let indent = 0;
      const indentSize = 4;

      const formattedLines = lines.map((line) => {
        const trimmed = line.trim();
        if (trimmed.startsWith("</")) {
          indent = Math.max(0, indent - indentSize);
        }
        const result = " ".repeat(indent) + trimmed;
        if (
          trimmed.startsWith("<") &&
          !trimmed.startsWith("</") &&
          !trimmed.endsWith("/>")
        ) {
          indent += indentSize;
        }
        return result;
      });

      handleInputChange("htmlContent", formattedLines.join("\n"));
    } catch (error) {
      console.error("Error formatting HTML:", error);
    }
  };

  const handleModeSwitch = (newMode) => {
    // Warn user about potential loss when switching from HTML to visual
    if (
      editorMode === "html" &&
      newMode === "visual" &&
      formData.htmlContent.length > 0
    ) {
      const hasComplexHtml =
        formData.htmlContent.includes("<!DOCTYPE") ||
        formData.htmlContent.includes("table-cell") ||
        formData.htmlContent.includes("style=");

      if (hasComplexHtml) {
        const confirmSwitch = window.confirm(
          "Switching to Visual Editor may remove complex styling and HTML structure. " +
            "Your original HTML will be preserved in Code/HTML modes. Continue?"
        );

        if (!confirmSwitch) {
          return;
        }

        // Backup the original HTML
        setOriginalHtmlContent(formData.htmlContent);
      }
    }

    // If switching back to HTML mode and we have a backup, offer to restore
    if (
      newMode === "html" &&
      originalHtmlContent &&
      originalHtmlContent !== formData.htmlContent
    ) {
      const restoreOriginal = window.confirm(
        "Would you like to restore your original HTML template? " +
          "This will replace any changes made in the Visual Editor."
      );

      if (restoreOriginal) {
        handleInputChange("htmlContent", originalHtmlContent);
      }
    }

    setEditorMode(newMode);
  };

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        type: template.type || "",
        subject: template.subject || "",
        htmlContent: template.htmlContent || "",
        textContent: template.textContent || "",
        status: template.status || EMAIL_TEMPLATE_STATUS.DRAFT,
        isDefault: template.isDefault || false,
        description: template.description || "",
        fromEmail: template.fromEmail || "",
        fromName: template.fromName || "",
        replyTo: template.replyTo || "",
        attachments: template.attachments || [],
      });
      if (template.variables) {
        setVariables(template.variables);
      }
    } else {
      // Reset form for new template creation
      setFormData({
        name: "",
        type: "",
        subject: "",
        htmlContent: "",
        textContent: "",
        status: EMAIL_TEMPLATE_STATUS.DRAFT,
        isDefault: false,
        description: "",
        fromEmail: "elio@starntop.com",
        fromName: "Elio & Papas",
        replyTo: "",
        attachments: [],
      });
      setVariables([]);
      setEditorMode("visual");
      setOriginalHtmlContent("");
    }
  }, [template, isOpen]);

  useEffect(() => {
    if (formData.type && templateVariables) {
      setLoadingVariables(true);
      templateVariables(formData.type)
        .then(setVariables)
        .catch(console.error)
        .finally(() => setLoadingVariables(false));
    }

    // Auto-populate HTML content with specific template when type is selected
    if (formData.type && !formData.htmlContent) {
      // Get the default template content for this specific type
      const defaultContent = getDefaultTemplateContent(formData.type);
      
      if (defaultContent) {
        setFormData((prev) => ({
          ...prev,
          htmlContent: defaultContent.htmlContent,
          subject: defaultContent.subject || prev.subject,
          textContent: defaultContent.textContent || prev.textContent,
        }));
      } else {
        // Fallback to basic template if no specific template found
        const basicTemplate = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${
      formData.type.charAt(0).toUpperCase() + formData.type.slice(1)
    } - Elio & Papas</title>
    <style>
        @media only screen and (max-width: 600px) {
            .container { width: 100% !important; }
            .content { padding: 20px !important; }
            .header { padding: 20px !important; }
        }
    </style>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f8fafc; line-height: 1.6;">
    <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);">
        
        <!-- Header -->
        <div style="background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); padding: 40px 30px; text-align: center; position: relative;">
            <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" width=\"100\" height=\"100\" patternUnits=\"userSpaceOnUse\"><circle cx=\"50\" cy=\"50\" r=\"1\" fill=\"white\" opacity=\"0.1\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>'); opacity: 0.3;"></div>
            <div style="position: relative; z-index: 1;">
                <div style="font-size: 48px; margin-bottom: 10px;">🏢</div>
                <h1 style="margin: 0; color: #ffffff; font-size: 32px; font-weight: 700; letter-spacing: -0.5px;">Elio & Papas</h1>
                <p style="margin: 8px 0 0 0; color: #e0e7ff; font-size: 16px; font-weight: 400;">${
                  formData.type.charAt(0).toUpperCase() + formData.type.slice(1)
                }</p>
            </div>
        </div>
        
        <!-- Main Content -->
        <div style="padding: 50px 40px; background-color: #ffffff;">
            <!-- Greeting -->
            <div style="margin-bottom: 30px;">
                <p style="color: #374151; font-size: 18px; margin: 0 0 20px 0; font-weight: 500;">Dear {{user_name}},</p>
                <p style="color: #6b7280; font-size: 16px; margin: 0; line-height: 1.7;">
                    Your email content goes here. Use the variables panel to insert dynamic content like {{first_name}}, {{email}}, etc.
                </p>
            </div>
            
            <!-- Content Card -->
            <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 12px; padding: 30px; margin: 30px 0; border-left: 4px solid #3b82f6;">
                <h3 style="color: #1e40af; font-size: 20px; margin: 0 0 15px 0; font-weight: 600;">
                    <span style="font-size: 24px; margin-right: 8px;">📋</span>Important Information
                </h3>
                <p style="color: #4b5563; font-size: 15px; margin: 0; line-height: 1.6;">
                    This is where you can highlight important information or key details for your recipients.
                </p>
            </div>
            
            <!-- Call to Action Button -->
            <div style="text-align: center; margin: 40px 0;">
                <a href="{{action_url}}" style="display: inline-block; padding: 18px 36px; background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); color: #ffffff; text-decoration: none; border-radius: 12px; font-weight: 600; font-size: 16px; box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4); transition: all 0.3s ease;">
                    <span style="font-size: 18px; margin-right: 8px;">🚀</span>Call to Action
                </a>
            </div>
            
            <!-- Additional Content -->
            <div style="background-color: #f9fafb; border-radius: 8px; padding: 25px; margin: 30px 0;">
                <h4 style="color: #374151; font-size: 16px; margin: 0 0 12px 0; font-weight: 600;">
                    <span style="font-size: 20px; margin-right: 8px;">📄</span>Additional Details
                </h4>
                <p style="color: #6b7280; font-size: 14px; margin: 0; line-height: 1.6;">
                    You can add more content sections here as needed for your email template.
                </p>
            </div>
        </div>
        
        <!-- Footer -->
        <div style="background-color: #f8fafc; padding: 30px 40px; border-top: 1px solid #e5e7eb;">
            <div style="text-align: center; margin-bottom: 20px;">
                <p style="color: #374151; font-size: 16px; margin: 0 0 8px 0; font-weight: 500;">
                    Best regards,<br>
                    <strong style="color: #1e40af;">Elio & Papas Team</strong>
                </p>
            </div>
            
            <!-- Contact Info -->
            <div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin: 20px 0;">
                <div style="display: flex; justify-content: center; gap: 30px; flex-wrap: wrap;">
                    <div style="text-align: center;">
                        <p style="color: #6b7280; font-size: 12px; margin: 0 0 4px 0; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px;">
                            <span style="font-size: 14px; margin-right: 4px;">📧</span>Email
                        </p>
                        <p style="color: #1e40af; font-size: 14px; margin: 0; font-weight: 500;">elio@starntop.com</p>
                    </div>
                    <div style="text-align: center;">
                        <p style="color: #6b7280; font-size: 12px; margin: 0 0 4px 0; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px;">
                            <span style="font-size: 14px; margin-right: 4px;">🌐</span>Website
                        </p>
                        <p style="color: #1e40af; font-size: 14px; margin: 0; font-weight: 500;">starntop.com</p>
                    </div>
                </div>
            </div>
            
            <!-- Copyright -->
            <div style="text-align: center; margin-top: 20px;">
                <p style="color: #9ca3af; font-size: 12px; margin: 0;">
                    © 2025 Elio & Papas. All rights reserved.
                </p>
            </div>
        </div>
    </div>
</body>
</html>`;

        setFormData((prev) => ({
          ...prev,
          htmlContent: basicTemplate,
        }));
      }
    }
  }, [formData.type, templateVariables]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    // Debug: Log the form data being validated

    // Validate the form data before saving
    const validation = validateEmailTemplate(formData);

    if (!validation.isValid) {
      // Show validation errors
      const errorMessages = Object.values(validation.errors).join("; ");
      console.error("Validation errors:", validation.errors);
      toast({
        title: "Validation Error",
        description: errorMessages,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      ...formData,
      variables,
    };
    
    onSave(payload);
  };

  const handleClose = () => {
    // Reset form state when closing
    setFormData({
      name: "",
      type: "",
      subject: "",
      htmlContent: "",
      textContent: "",
      status: EMAIL_TEMPLATE_STATUS.DRAFT,
      isDefault: false,
      description: "",
      fromEmail: "elio@starntop.com",
      fromName: "Elio & Papas",
      replyTo: "",
      attachments: [],
    });
    setVariables([]);
    setEditorMode("visual");
    setOriginalHtmlContent("");
    onClose();
  };

  const insertVariable = (variable) => {
    if (editorMode === "visual" && quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      const index = range ? range.index : quill.getLength();
      quill.insertText(index, variable);
      quill.setSelection(index + variable.length);
    } else {
      // Fallback for code mode
      const textarea = document.getElementById("htmlContentCode");
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = formData.htmlContent;
        const before = text.substring(0, start);
        const after = text.substring(end, text.length);
        const newText = before + variable + after;

        handleInputChange("htmlContent", newText);

        setTimeout(() => {
          textarea.setSelectionRange(
            start + variable.length,
            start + variable.length
          );
          textarea.focus();
        }, 0);
      }
    }
  };

  const processPreviewContent = (content) => {
    // Replace variables with sample data for preview
    let processedContent = content;
    variables.forEach((variable) => {
      const variableName = variable.replace(/[{}]/g, "");
      const sampleValue = getSampleValueForVariable(variableName);
      processedContent = processedContent.replace(
        new RegExp(variable.replace(/[{}]/g, "\\{\\}"), "g"),
        sampleValue
      );
    });
    return processedContent;
  };

  const getSampleValueForVariable = (variableName) => {
    const sampleData = {
      first_name: "John",
      last_name: "Doe",
      email: "john.doe@example.com",
      company: "Acme Corp",
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
    };
    return sampleData[variableName] || `[${variableName}]`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={{ base: "full", md: "6xl", lg: "full" }}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="100vh" m={{ base: 2, md: 4 }}>
        <ModalHeader>
          {template ? "Edit Email Template" : "Create New Email Template"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Validation Summary */}
          <Box
            bg="orange.50"
            border="1px"
            borderColor="orange.200"
            borderRadius="md"
            p={{ base: 2, md: 3 }}
            mb={4}
          >
            <Text fontSize="sm" color="orange.700" fontWeight="medium">
              ⚠️ Required Fields
            </Text>
            <Text fontSize="xs" color="orange.600" mt={1}>
              Template Name, Template Type, Email Subject, From Email, and HTML
              Content are required fields.
            </Text>
            <Text
              fontSize="xs"
              color="orange.600"
              mt={1}
              display={{ base: "block", md: "none" }}
            >
              💡 Tip: On mobile, use the HTML mode for better control over your
              email template.
            </Text>
          </Box>

          <VStack spacing={4} align="stretch">
            {/* Basic Information */}
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={4}
            >
              <FormControl isRequired>
                <FormLabel>Template Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter template name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Template Type</FormLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                  placeholder="Select template type"
                >
                  {templateTypes?.map((type) => (
                    <option key={type.type} value={type.type}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <FormControl isRequired>
              <FormLabel>Email Subject</FormLabel>
              <Input
                value={formData.subject}
                onChange={(e) => handleInputChange("subject", e.target.value)}
                placeholder="Enter email subject"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Required: Subject line for your email
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Template description"
                rows={{ base: 3, md: 2 }}
                resize="vertical"
              />
            </FormControl>

            {/* Email Configuration Info */}
            <Box
              bg="blue.50"
              border="1px"
              borderColor="blue.200"
              borderRadius="md"
              p={{ base: 2, md: 3 }}
            >
              <Text fontSize="sm" color="blue.700" fontWeight="medium">
                📧 Email Configuration
              </Text>
              <Text fontSize="xs" color="blue.600" mt={1}>
                Configure the sender information for your email templates. The
                "From Email" field is required and must be a valid email
                address.
              </Text>
              <Text
                fontSize="xs"
                color="blue.600"
                mt={1}
                display={{ base: "block", md: "none" }}
              >
                📱 Mobile: Fields stack vertically for easier input.
              </Text>
            </Box>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={4}
            >
              <FormControl
                isRequired
                isInvalid={
                  !formData.fromEmail ||
                  (formData.fromEmail &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail))
                }
              >
                <FormLabel>From Email</FormLabel>
                <Input
                  type="email"
                  value={formData.fromEmail}
                  onChange={(e) =>
                    handleInputChange("fromEmail", e.target.value)
                  }
                  placeholder="sender@example.com"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Required: Must be a valid email address (e.g.,
                  sender@example.com)
                </Text>
                {formData.fromEmail &&
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail) && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      Please enter a valid email address
                    </Text>
                  )}
              </FormControl>

              <FormControl>
                <FormLabel>From Name</FormLabel>
                <Input
                  value={formData.fromName}
                  onChange={(e) =>
                    handleInputChange("fromName", e.target.value)
                  }
                  placeholder="Sender Name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Reply To</FormLabel>
                <Input
                  type="email"
                  value={formData.replyTo}
                  onChange={(e) => handleInputChange("replyTo", e.target.value)}
                  placeholder="reply@example.com"
                />
              </FormControl>
            </Grid>

            {/* Attachments Section */}
            <Box>
              <FormLabel>Attachments (Optional)</FormLabel>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Add file attachments to your email template. Supported formats:
                PDF, DOC, DOCX, JPG, PNG, GIF
              </Text>
              <Box
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="md"
                p={4}
                textAlign="center"
                bg="gray.50"
                _hover={{ bg: "gray.100" }}
                cursor="pointer"
                onClick={() => {
                  // For now, just show a placeholder - file upload functionality can be added later
                  toast({
                    title: "File Upload",
                    description:
                      "File upload functionality will be implemented in a future update.",
                    status: "info",
                    duration: 3000,
                    isClosable: true,
                  });
                }}
              >
                <Text color="gray.500" fontSize="sm">
                  📎 Click to add attachments or drag files here
                </Text>
                <Text color="gray.400" fontSize="xs" mt={1}>
                  Currently {formData.attachments.length} attachment(s) added
                </Text>
              </Box>
              {formData.attachments.length > 0 && (
                <Box mt={2}>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Current Attachments:
                  </Text>
                  <VStack spacing={1} align="stretch">
                    {formData.attachments.map((attachment, index) => (
                      <Flex
                        key={index}
                        justify="space-between"
                        align="center"
                        p={2}
                        bg="gray.100"
                        borderRadius="md"
                      >
                        <Text fontSize="sm">
                          {attachment.name || `Attachment ${index + 1}`}
                        </Text>
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => {
                            const newAttachments = formData.attachments.filter(
                              (_, i) => i !== index
                            );
                            handleInputChange("attachments", newAttachments);
                          }}
                        >
                          Remove
                        </Button>
                      </Flex>
                    ))}
                  </VStack>
                </Box>
              )}
            </Box>

            <Grid
              templateColumns={{ base: "1fr", md: "1fr auto" }}
              gap={4}
              alignItems="end"
            >
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                >
                  <option value={EMAIL_TEMPLATE_STATUS.DRAFT}>Draft</option>
                  <option value={EMAIL_TEMPLATE_STATUS.ACTIVE}>Active</option>
                  <option value={EMAIL_TEMPLATE_STATUS.INACTIVE}>
                    Inactive
                  </option>
                </Select>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Set as Default</FormLabel>
                <Switch
                  isChecked={formData.isDefault}
                  onChange={(e) =>
                    handleInputChange("isDefault", e.target.checked)
                  }
                />
              </FormControl>
            </Grid>

            {/* Variables Section */}
            {formData.type && (
              <Box>
                <FormLabel>Available Variables</FormLabel>
                {loadingVariables ? (
                  <Spinner size="sm" />
                ) : (
                  <Flex
                    wrap="wrap"
                    gap={2}
                    justify={{ base: "center", md: "flex-start" }}
                  >
                    {variables.map((variable, index) => (
                      <Button
                        key={index}
                        size="sm"
                        variant="outline"
                        onClick={() => insertVariable(variable)}
                        flex={{ base: "1 1 auto", md: "none" }}
                        minW={{ base: "auto", md: "auto" }}
                      >
                        {variable}
                      </Button>
                    ))}
                  </Flex>
                )}
              </Box>
            )}

            <Divider />

            {/* Email Content Editor with Preview */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Text fontSize="lg" fontWeight="semibold">
                  Email Content *
                </Text>
                <HStack>
                  <Tooltip label="Visual Editor - May strip complex HTML">
                    <IconButton
                      icon={<EditIcon />}
                      size="sm"
                      variant={editorMode === "visual" ? "solid" : "outline"}
                      onClick={() => handleModeSwitch("visual")}
                    />
                  </Tooltip>
                  <Tooltip label="Rich Text Code - ReactQuill output">
                    <IconButton
                      icon={<Code />}
                      size="sm"
                      variant={editorMode === "code" ? "solid" : "outline"}
                      onClick={() => handleModeSwitch("code")}
                    />
                  </Tooltip>
                  <Tooltip label="Raw HTML - Full control">
                    <IconButton
                      icon={
                        <Box as="span" fontSize="xs" fontWeight="bold">
                          HTML
                        </Box>
                      }
                      size="sm"
                      variant={editorMode === "html" ? "solid" : "outline"}
                      onClick={() => handleModeSwitch("html")}
                    />
                  </Tooltip>
                </HStack>
              </HStack>
              <Text fontSize="sm" color="gray.600" mb={2}>
                HTML content is required. Use the templates below or create your
                own HTML email.
              </Text>

              {/* Mode Warning */}
              {editorMode === "visual" && originalHtmlContent && (
                <Box
                  bg="orange.50"
                  border="1px"
                  borderColor="orange.200"
                  borderRadius="md"
                  p={{ base: 2, md: 3 }}
                  mb={4}
                >
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="orange.700">
                      ⚠️ Visual Editor may have simplified your HTML. Switch to
                      HTML mode to restore original formatting.
                    </Text>
                    <Button
                      size="xs"
                      colorScheme="orange"
                      variant="outline"
                      onClick={() => handleModeSwitch("html")}
                      alignSelf={{ base: "stretch", md: "flex-start" }}
                    >
                      Restore
                    </Button>
                  </VStack>
                </Box>
              )}

              {editorMode === "html" && (
                <Box
                  bg="blue.50"
                  border="1px"
                  borderColor="blue.200"
                  borderRadius="md"
                  p={{ base: 2, md: 3 }}
                  mb={4}
                >
                  <Text fontSize="sm" color="blue.700">
                    💡 <strong>HTML Mode:</strong> Perfect for complex email
                    templates with custom styling. Visual Editor may strip
                    advanced formatting when switching modes.
                  </Text>
                  <Text
                    fontSize="xs"
                    color="blue.600"
                    mt={1}
                    display={{ base: "block", md: "none" }}
                  >
                    📱 Mobile: HTML mode gives you full control on small
                    screens.
                  </Text>
                </Box>
              )}

              <Grid
                templateColumns={{ base: "1fr", lg: "250px 1fr" }}
                gap={{ base: 4, lg: 6 }}
                minH={{ base: "auto", lg: "500px" }}
              >
                {/* Sidebar with Quick Insert */}
                <Box order={{ base: 2, lg: 1 }}>
                  <Text fontWeight="medium" mb={4}>
                    Tools & Templates
                  </Text>
                  {editorMode === "visual" && <QuickInsertButtons />}

                  {/* HTML Templates for HTML mode */}
                  {editorMode === "html" && (
                    <VStack spacing={3} align="stretch">
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Email Templates
                      </Text>
                      <VStack spacing={2} align="stretch">
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          display={{ base: "block", lg: "none" }}
                        >
                          Email Templates (Mobile)
                        </Text>
                        <HStack
                          spacing={2}
                          flexWrap="wrap"
                          display={{ base: "flex", lg: "none" }}
                        >
                          <Button
                            variant="outline"
                            onClick={() => loadHtmlTemplate("welcome")}
                            size="sm"
                            flex="1 1 auto"
                          >
                            📧 Welcome Email
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => loadHtmlTemplate("newsletter")}
                            size="sm"
                            flex="1 1 auto"
                          >
                            📰 Newsletter
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => loadHtmlTemplate("promotional")}
                            size="sm"
                            flex="1 1 auto"
                          >
                            🧾 Transactional
                          </Button>
                        </HStack>

                        <Text
                          fontSize="xs"
                          color="gray.500"
                          display={{ base: "none", lg: "block" }}
                        >
                          Email Templates (Desktop)
                        </Text>
                        <ButtonGroup
                          size="sm"
                          spacing={2}
                          orientation="vertical"
                          display={{ base: "none", lg: "flex" }}
                        >
                          <Button
                            variant="outline"
                            onClick={() => loadHtmlTemplate("welcome")}
                            textAlign="left"
                            justifyContent="flex-start"
                          >
                            📧 Welcome Email
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => loadHtmlTemplate("newsletter")}
                            textAlign="left"
                            justifyContent="flex-start"
                          >
                            📰 Newsletter
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => loadHtmlTemplate("promotional")}
                            textAlign="left"
                            justifyContent="flex-start"
                          >
                            🧾 Transactional
                          </Button>
                        </ButtonGroup>
                      </VStack>

                      <Divider my={2} />

                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        Actions
                      </Text>
                      <VStack spacing={2} align="stretch">
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          display={{ base: "block", lg: "none" }}
                        >
                          Actions (Mobile)
                        </Text>
                        <HStack
                          spacing={2}
                          flexWrap="wrap"
                          display={{ base: "flex", lg: "none" }}
                        >
                          <Button
                            variant="outline"
                            onClick={() => handleInputChange("htmlContent", "")}
                            colorScheme="red"
                            size="sm"
                            flex="1 1 auto"
                          >
                            🗑️ Clear All
                          </Button>
                          <Button
                            variant="outline"
                            onClick={formatHtmlCode}
                            size="sm"
                            flex="1 1 auto"
                          >
                            ✨ Format Code
                          </Button>
                          {originalHtmlContent &&
                            originalHtmlContent !== formData.htmlContent && (
                              <Button
                                variant="outline"
                                onClick={() =>
                                  handleInputChange(
                                    "htmlContent",
                                    originalHtmlContent
                                  )
                                }
                                colorScheme="blue"
                                size="sm"
                                flex="1 1 auto"
                              >
                                🔄 Restore Original
                              </Button>
                            )}
                        </HStack>

                        <Text
                          fontSize="xs"
                          color="gray.500"
                          display={{ base: "none", lg: "block" }}
                        >
                          Actions (Desktop)
                        </Text>
                        <ButtonGroup
                          size="sm"
                          spacing={2}
                          orientation="vertical"
                          display={{ base: "none", lg: "flex" }}
                        >
                          <Button
                            variant="outline"
                            onClick={() => handleInputChange("htmlContent", "")}
                            colorScheme="red"
                            textAlign="left"
                            justifyContent="flex-start"
                          >
                            🗑️ Clear All
                          </Button>
                          <Button
                            variant="outline"
                            onClick={formatHtmlCode}
                            textAlign="left"
                            justifyContent="flex-start"
                          >
                            ✨ Format Code
                          </Button>
                          {originalHtmlContent &&
                            originalHtmlContent !== formData.htmlContent && (
                              <Button
                                variant="outline"
                                onClick={() =>
                                  handleInputChange(
                                    "htmlContent",
                                    originalHtmlContent
                                  )
                                }
                                colorScheme="blue"
                                textAlign="left"
                                justifyContent="flex-start"
                              >
                                🔄 Restore Original
                              </Button>
                            )}
                        </ButtonGroup>
                      </VStack>
                    </VStack>
                  )}
                </Box>

                {/* Editor Panel */}
                <Box order={{ base: 1, lg: 2 }}>
                  <VStack spacing={2} mb={2}>
                    <HStack justify="space-between" w="full">
                      <Text fontWeight="medium">
                        {editorMode === "visual" && "Visual Editor"}
                        {editorMode === "code" && "Rich Text Code"}
                        {editorMode === "html" && "Raw HTML Editor"}
                      </Text>
                      {editorMode === "html" && (
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          display={{ base: "none", md: "block" }}
                        >
                          Paste complete HTML templates here
                        </Text>
                      )}
                    </HStack>
                    {editorMode === "html" && (
                      <Text
                        fontSize="xs"
                        color="gray.500"
                        display={{ base: "block", md: "none" }}
                      >
                        Paste complete HTML templates here
                      </Text>
                    )}
                  </VStack>

                  <Box border="1px" borderColor="gray.200" borderRadius="md">
                    {editorMode === "visual" ? (
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={formData.htmlContent}
                        onChange={(content) =>
                          handleInputChange("htmlContent", content)
                        }
                        modules={getResponsiveModules()}
                        formats={formats}
                        style={{ height: { base: "300px", md: "400px" } }}
                        placeholder="Start creating your email template..."
                      />
                    ) : editorMode === "code" ? (
                      <Textarea
                        id="htmlContentCode"
                        value={formData.htmlContent}
                        onChange={(e) =>
                          handleInputChange("htmlContent", e.target.value)
                        }
                        placeholder="Enter HTML email content from the visual editor"
                        fontFamily="monospace"
                        fontSize="sm"
                        minH={{ base: "300px", md: "450px" }}
                        border="none"
                        borderRadius="md"
                        resize="none"
                      />
                    ) : (
                      <Textarea
                        value={formData.htmlContent}
                        onChange={(e) =>
                          handleInputChange("htmlContent", e.target.value)
                        }
                        placeholder="Paste your complete HTML email template here...

Example:
<!DOCTYPE html>
<html>
<head>
    <meta charset='utf-8'>
    <title>Email Template</title>
</head>
<body>
    <div style='font-family: Arial, sans-serif;'>
        <h1>Welcome {{first_name}}!</h1>
        <p>Your email content here...</p>
    </div>
</body>
</html>"
                        fontFamily="monospace"
                        fontSize="sm"
                        minH={{ base: "300px", md: "450px" }}
                        border="none"
                        borderRadius="md"
                        resize="none"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Preview Panel - Full Width Below */}
              <Box mt={6}>
                <Text fontWeight="medium" mb={2}>
                  Live Preview
                </Text>
                <Box
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  minH={{ base: "200px", md: "300px" }}
                  bg="white"
                  overflow="auto"
                >
                  {/* Email Headers Preview */}
                  <Box
                    p={{ base: 2, md: 3 }}
                    bg="gray.50"
                    borderBottom="1px"
                    borderColor="gray.200"
                  >
                    <VStack spacing={1} align="stretch">
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        wordBreak="break-all"
                      >
                        <strong>From:</strong> {formData.fromName} &lt;
                        {formData.fromEmail}&gt;
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        wordBreak="break-all"
                      >
                        <strong>Subject:</strong>{" "}
                        {processPreviewContent(formData.subject)}
                      </Text>
                      {formData.replyTo && (
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          wordBreak="break-all"
                        >
                          <strong>Reply-To:</strong> {formData.replyTo}
                        </Text>
                      )}
                    </VStack>
                  </Box>

                  {/* Email Content Preview */}
                  <Box p={{ base: 2, md: 4 }}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: processPreviewContent(formData.htmlContent),
                      }}
                      style={{
                        fontFamily: "Arial, sans-serif",
                        lineHeight: "1.6",
                        color: "#333",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Plain Text Content */}
            <FormControl>
              <FormLabel>Plain Text Content (Optional)</FormLabel>
              <Textarea
                value={formData.textContent}
                onChange={(e) =>
                  handleInputChange("textContent", e.target.value)
                }
                placeholder="Enter plain text version (auto-generated if left empty)"
                rows={{ base: 5, md: 4 }}
                resize="vertical"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <VStack spacing={3} w="full" align="stretch">
            {/* Button Row 1 - Main Actions */}
            <HStack justify="space-between" w="full">
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <HStack>
                {!template && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFormData({
                        name: "",
                        type: "",
                        subject: "",
                        htmlContent: "",
                        textContent: "",
                        status: EMAIL_TEMPLATE_STATUS.DRAFT,
                        isDefault: false,
                        description: "",
                        fromEmail: "noreply@yourcompany.com",
                        fromName: "Your Company",
                        replyTo: "",
                        attachments: [],
                      });
                      setVariables([]);
                    }}
                  >
                    Clear Form
                  </Button>
                )}
                <Button
                  colorScheme="blue"
                  onClick={handleSave}
                  isLoading={loading}
                  loadingText="Saving..."
                >
                  {template ? "Update" : "Create"} Template
                </Button>
              </HStack>
            </HStack>

            {/* Button Row 2 - Validation Test */}
            <Button
              variant="outline"
              onClick={() => {
                const validation = validateEmailTemplate(formData);
                if (!validation.isValid) {
                  const errorMessages = Object.values(validation.errors).join(
                    "; "
                  );
                  toast({
                    title: "Validation Check",
                    description: errorMessages,
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                  });
                } else {
                  toast({
                    title: "Validation Passed",
                    description: "All required fields are filled correctly!",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                  });
                }
              }}
            >
              Test Validation
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TemplateFormModal;
